/* eslint-disable max-len */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AdditionalInformation } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/AdditionalInformation';
import { ReferenceBrandType } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceBrandType';
import { VehicleBrandType } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { DealerType } from '@1po/1po-bff-fe-spec/generated/user/model/CommercialLink';
import { v4 as uuidv4 } from 'uuid';
import { ROUTER_CATALOG_DH_L3, ROUTER_CATALOG_VEHICLE, ROUTER_PRODUCT } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { InfoCircleIcon } from 'assets/icons';
import { AddToEstimateButtonAndDialog } from 'components/AddToEstimate/AddToEstimateButtonAndDialog';
import { CopyReferenceWithFeedback, CopySelectionWrapper } from 'components/CopyReferenceFeedback';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { DiscountCorner } from 'components/Discount';
import { DocumentationAlertButton } from 'components/DocumentationAlertButton';
import { FirstHelpPopin } from 'components/Help/FirstHelpPopin';
import ProductModal from 'components/ProductModal';
import { ReferencePriceSection } from 'components/ReferencePriceSection';
import { DeliveryInformationUnavailable } from 'components/ReferenceUnavailableBox/DeliveryInformationUnavailable';
import {
  ReferenceUnavailable,
  ReferenceUnavailableForOrder,
} from 'components/ReferenceUnavailableBox/ReferenceUnavailable';
import StockDisplay from 'components/StockInfo';
import { getTechnicalCharacteristicsLabel } from 'components/StockInfo/catalogStockUtils';
import PrivateComponent from 'composers/PrivateComponent';
import { getLastSearchedVehicleKey, getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import {
  DATAHUB,
  PlateIdParam,
  RENATIVE_LABEL,
  RENATIVE_TECHNICAL_CHARACTERISTICS_CODE,
  STANDARD,
} from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { useFetchLaborTimeIdsByReferenceGenericPart } from 'domains/laborTime/LaborTime.requests';
import { getLaborTimeIdsForGenericPart } from 'domains/laborTime/LaborTime.store';
import {
  ADDITIONAL_INFO_DISPLAY_STYLE,
  formatReferenceLabel,
  getDHReference,
  getIsStockAvailable,
  ReferencePriceType,
} from 'domains/references';
import { useFetchEquivalentReferences } from 'domains/references/References.requests';
import { getLatestInStockReference } from 'domains/references/References.store';
import { GarageView, getDealerType, getUserCountry, getUserRights, SparePartsViewType, UserRole } from 'domains/user';
import { SupersessionTooltip } from 'pages/CatalogPage/common/ReferenceSection/SupersessionTooltip';
import { BrandImage } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/BrandImage';
import ElectronicPartsRepairLink from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ElectronicPartsRepairLink';
import Exadis from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/Exadis';
import LinkedReferences from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/LinkedReferences/LinkedReferences';
import ReferenceCardLaborTimesWrapper from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReferenceCardLaborTimesWrapper';
import Reuse from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/Reuse';
import ReuseAvailabilitiesButton from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReuseAvailabilities/ReuseAvailabilitiesButton';
import { EquivalentSection } from 'pages/CatalogPage/IAM/FullTextSearchResult/SearchResultSection';
import { theme } from 'styles';
import {
  Box,
  CenteredSpin,
  Flex,
  Icon,
  Link,
  LinkRoundButton,
  MarginBox,
  MinusCircleButton,
  PlusCircleButton,
  sentenceCase,
  Text,
} from 'UI';
import { ShowMoreButtonWrapper } from 'UI/Descriptions/Descriptions.styled';
import { ERROR, getData, isTractionBatteryOrderBlocked, LOADING, NOT_FOUND, useExtraLarge } from 'utils';
import { Country } from 'utils/i18n/Country';
import {
  AdditionalInfoExpandableWrapper,
  ReferenceImageContainer,
  SBrandImageBox,
  SCard,
} from './ReferenceCard.styled';
import { SeePlateLink } from './SeePlateLink';

const BrandImageWrapper = ({
  availableInUserCountry,
  children,
}: {
  availableInUserCountry: boolean;
  children: React.ReactNode;
}) => (availableInUserCountry ? <>{children}</> : <SBrandImageBox>{children}</SBrandImageBox>);

const AdditionalInfoInsideCard = ({
  additionalInfo,
  availableInUserCountry,
  extraLarge,
  onNavigate,
}: {
  additionalInfo?: AdditionalInformation[];
  availableInUserCountry: boolean;
  extraLarge: boolean;
  onNavigate: () => void;
}) => {
  const { t } = useTranslation();
  if (!additionalInfo || additionalInfo.length === 0) {
    return null;
  }
  return (
    <DataContainer data={additionalInfo}>
      <Flex
        minWidth={195}
        direction={'column'}
        align={'flex-start'}
        justify={'flex-end'}
        data-cy={'additional-information'}
      >
        <Flex
          direction={'column'}
          size={1}
          minWidth={80}
          maxWidth={290}
          maxHeight={150}
          justify={'center'}
          align={'flex-start'}
        >
          <Text type="base" bold color={availableInUserCountry ? undefined : 'grey55'} disableGutter>
            {t('catalog.details.additional_information', 'Additional information')}
          </Text>
          <Text type="sm" color={availableInUserCountry ? 'grey35' : 'grey55'}>
            {`${additionalInfo[0].title ? sentenceCase(additionalInfo[0].title.toLowerCase()) : ''}
                ${sentenceCase(mapAdditionalInfoValue(additionalInfo[0]).toLowerCase())} `}
          </Text>
          {additionalInfo.length > 1 && (
            <Text type="sm" color={availableInUserCountry ? 'grey35' : 'grey55'}>
              {`${additionalInfo[1].title ? sentenceCase(additionalInfo[1].title.toLowerCase()) : ''} ${sentenceCase(
                mapAdditionalInfoValue(additionalInfo[1]).toLowerCase(),
              )}`}
            </Text>
          )}
          {availableInUserCountry && (
            <Text onClick={onNavigate} type="sm" displayStyle={'link'} cursor={'pointer'} decoration={'underline'}>
              {additionalInfo.length > 2 && t('catalog.reference_card.additional_info.seeMore', 'See more...')}
            </Text>
          )}
          <MarginBox mr={20} mb={extraLarge ? 5 : 0} />
        </Flex>
      </Flex>
    </DataContainer>
  );
};

const AdditionalInfoExpandable = ({
  additionalInfo,
  showAdditionalInfo,
  onToggle,
}: {
  additionalInfo?: AdditionalInformation[];
  showAdditionalInfo: boolean;
  onToggle: () => void;
}) => {
  if (!additionalInfo || additionalInfo.length === 0) {
    return null;
  }
  return (
    <>
      {showAdditionalInfo ? (
        <>
          <AdditionalInfoExpandableWrapper>
            {additionalInfo.map((info) => (
              <>
                <Flex key={`additional-info-${uuidv4()}`}>
                  <Icon IconComponent={InfoCircleIcon} color={theme.color.grey55} size={21} noPointer />
                  <MarginBox ml={45} />
                  <Text type="base" bold color="grey35">
                    {info.title ? sentenceCase(info.title.toLowerCase()) : ''}
                  </Text>
                  <MarginBox ml={25} />
                  <Text type="base" color="grey35">
                    {sentenceCase(mapAdditionalInfoValue(info).toLowerCase())}
                  </Text>
                </Flex>
                <MarginBox mb={5} />
              </>
            ))}
          </AdditionalInfoExpandableWrapper>
          <ShowMoreButtonWrapper align={'center'} justify={'center'} maxHeight={0}>
            <MinusCircleButton onClick={onToggle} color={'grey55'} />
          </ShowMoreButtonWrapper>
        </>
      ) : (
        <ShowMoreButtonWrapper align={'center'} justify={'center'} maxHeight={0}>
          <PlusCircleButton onClick={onToggle} color={'ink_black'} />
        </ShowMoreButtonWrapper>
      )}
    </>
  );
};

const LaborTimeLoading = () => (
  <Box width={85}>
    <CenteredSpin size={'small'} />
  </Box>
);

const LaborTimeNotFound = ({ refIndex }: { refIndex?: number }) => {
  const { t } = useTranslation();
  return (
    <FirstHelpPopin
      streamId={ROUTER_CATALOG_DH_L3}
      popinId={`${ROUTER_CATALOG_DH_L3}_labor_times`}
      placement={'right'}
      skip={refIndex !== 0}
    >
      <Text type="sm" displayStyle={'disabled'}>
        {t('common.action.add_labor_time', 'Add Labor time')}
      </Text>
    </FirstHelpPopin>
  );
};

const LaborTimeError = () => {
  const { t } = useTranslation();
  return (
    <ErrorWithLabel
      label={t('common.labor_times.backend_error.short', 'Labor times temporarily unavailable.')}
      narrow
    />
  );
};

const LaborTimeLink = ({
  searchStatus,
  refIndex,
  onToggle,
}: {
  searchStatus: string | undefined;
  refIndex?: number;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();
  if (searchStatus === LOADING) {
    return <LaborTimeLoading />;
  }
  if (searchStatus === NOT_FOUND) {
    return <LaborTimeNotFound refIndex={refIndex} />;
  }
  if (searchStatus === ERROR) {
    return <LaborTimeError />;
  }
  return (
    <FirstHelpPopin
      streamId={ROUTER_CATALOG_DH_L3}
      popinId={`${ROUTER_CATALOG_DH_L3}_labor_times`}
      placement={'right'}
      skip={refIndex !== 0}
    >
      <Text hoverUnderLine onClick={onToggle} type="sm" displayStyle={'link'} cursor={'pointer'}>
        {t('common.action.add_labor_time', 'Add Labor time')}
      </Text>
    </FirstHelpPopin>
  );
};

export function mapAdditionalInfoValue(info: AdditionalInformation): string {
  if (info.description) {
    if (!info.id) {
      return info.description;
    }
    return `${info.description} (${info.id})`;
  }

  return info.id ?? '';
}

interface ReferenceCardProps {
  name?: string;
  referenceNumber: string;
  referenceBrand?: ReferenceBrandType;
  vehicleBrand: VehicleBrandType;
  price?: ReferencePriceType;
  sparePartsView: SparePartsViewType;
  displayPlateLink?: boolean;
  additionalInfo?: AdditionalInformation[];
  additionalInfoDisplayStyle?: ADDITIONAL_INFO_DISPLAY_STYLE;
  plateId?: string;
  linkedReferences: string[];
  isApplicableToCurrentVehicle: boolean | undefined;
  seePlateState?: [string | undefined, (x: string | undefined) => void];
  firstRefWithPrice?: string;
  firstRefWithPriceNotInCart?: string;
  refIndex?: number;
  renderLinkedReferencesSwitch: () => React.ReactNode;
  handleAddToCartClick: () => void;
  handleAddToEstimateClick: () => void;
  linkedReferencesActive: boolean;
  displaySupersessionTooltip?: boolean;
  availableForOrder?: boolean;
  genericPart?: string;
  isDiscontinued?: boolean;
  familyCode?: string;
}

const ReferenceCard = ({
  referenceNumber,
  name,
  referenceBrand,
  vehicleBrand,
  price,
  sparePartsView,
  displayPlateLink = false,
  additionalInfo,
  additionalInfoDisplayStyle = 'INSIDE_REFERENCE_CARD',
  linkedReferences,
  plateId,
  isApplicableToCurrentVehicle,
  seePlateState,
  firstRefWithPrice,
  firstRefWithPriceNotInCart,
  refIndex,
  genericPart,
  renderLinkedReferencesSwitch,
  handleAddToCartClick,
  linkedReferencesActive,
  handleAddToEstimateClick,
  displaySupersessionTooltip = false,
  availableForOrder = true,
  isDiscontinued = false,
  familyCode,
}: ReferenceCardProps) => {
  const { t } = useTranslation();
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const detail = useSelector((state: RootState) =>
    getDHReference(state, { vehicleKey: vehicleDetail?.vehicleKey, referenceNumber }),
  );
  const referenceData = getData(detail);
  const renative =
    getTechnicalCharacteristicsLabel(referenceData, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE) === RENATIVE_LABEL;
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const history = useHistory();
  const [laborTimesActive, setLaborTimesActive] = useState(false);
  const [showAdditionalInfoExpandable, setShowAdditionalInfoExpandable] = useState(false);
  const extraLarge = useExtraLarge();
  const availableInUserCountry = useSelector((state: RootState) => getIsStockAvailable(state, referenceNumber));
  const userRoles = useSelector(getUserRights);
  const resolvedUserRoles = Array.isArray(userRoles) ? userRoles : undefined;
  const orderBlocked = isTractionBatteryOrderBlocked(
    resolvedUserRoles,
    referenceData?.familyCode,
    vehicleDetail?.vin,
    referenceData?.isApplicableToCurrentVehicle,
  );
  const canOrder = availableForOrder && !orderBlocked;

  const country = useSelector(getUserCountry);
  const dealerType = useSelector(getDealerType);
  const allowedDealerTypes: DealerType[] = [UserRole.R1, UserRole.R2, UserRole.R3_MOTRIO];

  const equivalentReferences = useFetchEquivalentReferences(referenceNumber, isDiscontinued);

  useFetchLaborTimeIdsByReferenceGenericPart(referenceNumber, vehicleDetail?.vehicleKey);

  const laborTimeIdsWrapper = useSelector((state: RootState) =>
    getLaborTimeIdsForGenericPart(state, { vehicleKey, genericPart }),
  );

  // Always use getLatestInStockReference to ensure correct supersession display
  const latestInStockReference = useSelector((state: RootState) => getLatestInStockReference(state, referenceNumber));

  if (!vehicleDetail) {
    return null;
  }

  const productLink = plateId
    ? `${getCatalogSourceUrl(vehicleDetail.catalogSource)}/${
        vehicleDetail.vehicleKey
      }${ROUTER_CATALOG_VEHICLE}${ROUTER_PRODUCT}/${latestInStockReference}?${PlateIdParam}=${plateId}`
    : `${getCatalogSourceUrl(vehicleDetail.catalogSource)}/${
        vehicleDetail.vehicleKey
      }${ROUTER_CATALOG_VEHICLE}${ROUTER_PRODUCT}/${latestInStockReference}`;

  const toggleLaborTimesActive = () => {
    setLaborTimesActive((prev) => !prev);
  };

  const seePlateComp = (
    <>
      {displayPlateLink && detail && seePlateState && (
        <SeePlateLink referenceNumber={latestInStockReference} seePlateState={seePlateState} />
      )}
    </>
  );

  return (
    <>
      <SCard isSquared={laborTimesActive ?? !canOrder} data-cy={`dh-reference-card-${referenceNumber}`}>
        {sparePartsView === GarageView && <DiscountCorner reference={latestInStockReference} type={'catalog'} />}
        <Flex direction={'row'}>
          <MarginBox mr={20}>
            <Box width={120} height={120}>
              <Link to={productLink}>
                <BrandImageWrapper availableInUserCountry={availableInUserCountry}>
                  <BrandImage
                    referenceBrand={referenceBrand}
                    vehicleBrand={vehicleBrand}
                    familyCode={familyCode}
                    renative={renative}
                  />
                </BrandImageWrapper>
              </Link>
              {displaySupersessionTooltip && (
                <ProductModal
                  triggerComponent={
                    <ReferenceImageContainer>
                      <SupersessionTooltip>
                        <LinkRoundButton />
                      </SupersessionTooltip>
                    </ReferenceImageContainer>
                  }
                  referenceNumber={latestInStockReference}
                  seePlate={seePlateComp}
                  scrollToSubstitutes
                />
              )}
            </Box>
          </MarginBox>
          <Flex>
            <MarginBox mr={20}>
              <Flex wrap={'wrap'}>
                <Flex size={1} minWidth={extraLarge ? 315 : 0} direction={'column'}>
                  <Flex align={'center'}>
                    <ProductModal
                      triggerComponent={
                        <CopySelectionWrapper referenceNumber={latestInStockReference}>
                          <Text
                            type="heading3"
                            color={availableInUserCountry ? undefined : 'grey55'}
                            disableGutter
                            hoverUnderLine
                            cursor={'pointer'}
                            data-cy={'link-reference-number'}
                          >
                            {formatReferenceLabel(
                              t('catalog.reference_card.reference_number', 'Ref:'),
                              latestInStockReference,
                            )}
                          </Text>
                        </CopySelectionWrapper>
                      }
                      referenceNumber={latestInStockReference}
                      seePlate={seePlateComp}
                      isVehicleCatalog
                    />
                    <MarginBox mr={10} />
                    <CopyReferenceWithFeedback referenceNumber={latestInStockReference} />
                    {(referenceBrand === 'MOTRIO' || referenceBrand === 'IAM') && (
                      <>
                        <MarginBox mr={1} />
                        <DocumentationAlertButton
                          reference={getData(detail)}
                          vehicleDetail={vehicleDetail}
                          vehicleRegistrationNumber={vehicleDetail?.vrn ?? vehicleDetail?.vehicleKey}
                        />
                      </>
                    )}
                  </Flex>
                  <ProductModal
                    triggerComponent={
                      <Text
                        type="base"
                        bold={!availableInUserCountry}
                        color={availableInUserCountry ? 'grey35' : 'grey55'}
                        transform={'sentence-case'}
                        hoverUnderLine
                        cursor={'pointer'}
                        data-cy={'link-designation'}
                      >
                        {name}
                      </Text>
                    }
                    referenceNumber={latestInStockReference}
                    seePlate={seePlateComp}
                    isVehicleCatalog
                  />
                  {seePlateComp}
                  <StockDisplay
                    vehicleKey={vehicleDetail?.vehicleKey}
                    referenceNumber={latestInStockReference}
                    isApplicableToCurrentVehicle={isApplicableToCurrentVehicle}
                  />
                  {country === Country.FR.key && dealerType && allowedDealerTypes.includes(dealerType) && (
                    <Reuse referenceNumber={latestInStockReference} />
                  )}
                  <ReuseAvailabilitiesButton referenceNumber={latestInStockReference} />
                  <Exadis referenceNumber={referenceNumber} referenceBrand={referenceBrand} />
                  {!extraLarge && additionalInfoDisplayStyle === 'INSIDE_REFERENCE_CARD' && (
                    <AdditionalInfoInsideCard
                      additionalInfo={additionalInfo}
                      availableInUserCountry={availableInUserCountry}
                      extraLarge={extraLarge}
                      onNavigate={() => history.push(productLink)}
                    />
                  )}
                  {availableInUserCountry ? (
                    <Flex direction={'row'} justify={'space-between'} align={'center'} gap={16}>
                      {linkedReferences.length > 0 && renderLinkedReferencesSwitch()}
                      <LaborTimeLink
                        searchStatus={laborTimeIdsWrapper?.searchStatus}
                        refIndex={refIndex}
                        onToggle={toggleLaborTimesActive}
                      />
                      <ElectronicPartsRepairLink referenceNumber={latestInStockReference} type={'light_12'} />
                    </Flex>
                  ) : (
                    <DeliveryInformationUnavailable />
                  )}
                </Flex>
              </Flex>
            </MarginBox>
          </Flex>
          {extraLarge && additionalInfoDisplayStyle === 'INSIDE_REFERENCE_CARD' && (
            <AdditionalInfoInsideCard
              additionalInfo={additionalInfo}
              availableInUserCountry={availableInUserCountry}
              extraLarge={extraLarge}
              onNavigate={() => history.push(productLink)}
            />
          )}
          {availableInUserCountry ? (
            <Flex direction={'row'} justify={'flex-end'}>
              <ReferencePriceSection
                referenceNumber={latestInStockReference}
                sparePartsView={sparePartsView}
                handleAddToCartClick={handleAddToCartClick}
                vehicleKey={vehicleDetail?.vehicleKey}
                isApplicableToCurrentVehicle={referenceData?.isApplicableToCurrentVehicle ?? false}
                showFirstHelp={latestInStockReference === firstRefWithPriceNotInCart}
                align={'left'}
                catalogSource={DATAHUB}
                referenceType={STANDARD}
                availableForOrder={canOrder}
                linkedReferences={linkedReferences}
              />
              {price?.garageView?.vatExcludedPrice && (
                <PrivateComponent
                  render={() => (
                    <FirstHelpPopin
                      streamId={ROUTER_CATALOG_DH_L3}
                      popinId={`${ROUTER_CATALOG_DH_L3}_add_to_estimate`}
                      placement={'left'}
                      skip={latestInStockReference !== firstRefWithPrice}
                    >
                      <AddToEstimateButtonAndDialog
                        handleAddToEstimateClick={handleAddToEstimateClick}
                        displayed={canOrder}
                        isVehicleCatalog
                      />
                    </FirstHelpPopin>
                  )}
                  requiredRights={[UserRole.COMMAND, UserRole.CONNECT_COMMANDE]}
                />
              )}
            </Flex>
          ) : (
            <Flex minWidth={extraLarge ? 400 : 185} maxWidth={400} align={'center'} justify={'flex-end'}>
              <ReferenceUnavailable size={'medium'} />
            </Flex>
          )}
        </Flex>
        {linkedReferencesActive && (
          <Flex direction={'row'} data-cy={'linked-references'}>
            <LinkedReferences
              linkedReferences={[...linkedReferences].sort((a, b) => (a > b ? 1 : -1))}
              vehicleDetail={vehicleDetail}
              isApplicableToCurrentVehicle={isApplicableToCurrentVehicle}
              handleAddToCartClick={() => undefined}
              handleAddToEstimateClick={() => undefined}
              sparePartsView={sparePartsView}
            />
          </Flex>
        )}
      </SCard>
      {laborTimesActive && laborTimeIdsWrapper?.data && (
        <ReferenceCardLaborTimesWrapper
          referenceNumber={latestInStockReference}
          laborTimeIds={laborTimeIdsWrapper?.data}
          isSquared={!canOrder}
        />
      )}
      <ReferenceUnavailableForOrder enabled={!availableForOrder && !orderBlocked} />
      {additionalInfoDisplayStyle === 'EXPANDABLE_WITH_PLUS_ICON' && (
        <AdditionalInfoExpandable
          additionalInfo={additionalInfo}
          showAdditionalInfo={showAdditionalInfoExpandable}
          onToggle={() => setShowAdditionalInfoExpandable((prev) => !prev)}
        />
      )}
      <MarginBox mt={15} mb={10} />
      {equivalentReferences?.searchStatus === LOADING && (
        <MarginBox mt={15}>
          <CenteredSpin />
        </MarginBox>
      )}
      {equivalentReferences?.data && equivalentReferences.data.length > 0 && (
        <MarginBox mt={15}>
          <EquivalentSection
            sparePartsView={sparePartsView}
            equivalentReferences={equivalentReferences?.data}
            additionalEquivalencesLoading={false}
            hasMainReference={false}
            vehicleKey={vehicleKey}
          />
        </MarginBox>
      )}
      <MarginBox mt={15} />
    </>
  );
};
export default ReferenceCard;
